import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../styles/believer-achiever.css';
import { submitAssessmentResult } from '../services/formService';

/* ===================== The AI-First CHRO — Believer/Achiever assessment =====================
   React port of the standalone HTML page. Behaviour and copy are kept identical to the original;
   the vanilla-JS DOM logic is re-expressed with React state. All markup lives inside a
   `.bva-page` wrapper so the ported (scoped) stylesheet never collides with the site globals. */

/* Config — originally window.ASSESSMENT_CONFIG. Sourced from env so it can be set at build time.
   DATA_ENDPOINT: POST anonymous results here ("" = log locally only).
   DEEPER_ANALYSIS_URL: deeper analysis link ("" = shows "opens after the webinar"). */
const CFG = {
  DATA_ENDPOINT: process.env.REACT_APP_ASSESSMENT_DATA_ENDPOINT || '',
  DEEPER_ANALYSIS_URL: process.env.REACT_APP_DEEPER_ANALYSIS_URL || '',
};

/* Questions. Q1–Q10 place you on the Believer→Achiever spectrum; Q11 is the AI-Tourism question.
   Options run low → high. */
const QUESTIONS = [
  { t: 'How much AI is actually live in full production in your HR function — running day-to-day, not a pilot?',
    o: ['None yet — still exploring or evaluating', 'One or two pilots running; nothing live day-to-day', 'One use case live in production — say CV screening or an HR helpdesk bot', 'Several use cases live in production', 'AI is embedded across core HR processes as business-as-usual'] },
  { t: 'Can you show the business impact of your AI work with a number, measured against a baseline you set before you started?',
    o: ['Nothing deployed yet, so nothing to measure', 'No — we track activity (tools tried, pilots run), not a business result', 'Roughly — we have some numbers, but no clean before-baseline', 'Yes — at least one business metric moved (time-to-hire, cost-per-hire, 90-day attrition) against a baseline we set first', 'Yes — several metrics, tracked over time, and we can attribute the gain to the AI'] },
  { t: 'Before you chose any tool or vendor, did you define the HR problems you were trying to solve?',
    o: ["We haven't started", 'No — we started from tools and demos and worked backwards', 'Partly — some direction, but the tools led', 'Yes — we defined the problems and priorities first', 'Yes — and we revisit them on a set schedule'] },
  { t: 'How would you describe your AI work right now?',
    o: ['Nothing active yet', 'Lots of ideas and experiments running in parallel — a screening tool, a chatbot, an attrition model — none finished', 'Several running; hard to say which one matters most', 'Ranked by likely ROI, committed to one or two, paused the rest', 'Focused and sequenced, with a clear owner for each'] },
  { t: 'Before you start a pilot, do you decide up front what result will make you scale it, change it, or kill it?',
    o: ["We don't run structured pilots", 'No — we start and see how it goes', 'Sometimes, informally', 'Yes — we set scale/change/kill criteria before we start', "Yes — and we've actually killed or changed one on those criteria"] },
  { t: 'How is governance — bias checks, audit trails, human review, data privacy (DPDP Act) — handled?',
    o: ['Not yet relevant to us', "We'll deal with it if something goes wrong", 'Case by case, no set process', 'Built into the design before anything goes live', 'Formal protocols, owned and audited'] },
  { t: 'How much do you invest in change management — training, adoption, redesigning how people work — relative to the technology itself?',
    o: ['Nothing deployed yet', 'Little — we assumed recruiters and managers would just adopt it', 'Some, but far less than the tech spend', 'A meaningful, budgeted share — approaching the tech spend', 'Equal to or more than the tech spend, by design'] },
  { t: 'When you report on AI in HR, what do you mostly report?',
    o: ["We don't report on it", 'Activity — pilots launched, tools evaluated, people trained', 'A mix of activity and some outcomes', 'Mostly business outcomes — time-to-hire, cost, retention, hours returned', 'Outcomes the CFO or board recognise, tied to business goals'] },
  { t: 'Where does AI sit in your function today?',
    o: ['Not on the ground yet', 'A topic and an interest, not yet a project', 'A special project, with a steering committee', 'Becoming normal operations in some areas', 'Business-as-usual — no special label needed'] },
  { t: 'Could you hold an AI vendor accountable — ask the hard questions and read the real number — without relying on their demo?',
    o: ["No, and it hasn't come up", "No — we largely trust the vendor's framing", "Somewhat — we ask, but can't always verify", 'Yes — we know enough to challenge and verify', 'Yes — we set the metrics vendors must hit and hold them to it'] },
  { t: "In the last 12 months, roughly how many AI vendor demos, conference sessions, webinars or site visits has your team taken part in — none of which you've put into production?",
    o: ['0', '1–3', '4–7', '8+'] },
];

const STAGES = [
  { key: 'skeptic', name: 'Skeptic', group: 'Believer',
    read: "You want proof before you invest. Defensible — as long as it's a considered position, not avoidance. On the 21st, come for the evidence: what a real result looks like, and how to tell one from a vendor's slide." },
  { key: 'early', name: 'Early Believer', group: 'Believer',
    read: "You're convinced AI matters for HR, but it isn't on the ground yet. The gap now is action, not conviction. On the 21st: how to choose the first thing actually worth putting into production, and start small." },
  { key: 'active', name: 'Active Believer', group: 'Believer',
    read: "You're busy — pilots, demos, evaluations — but nothing is yet in full production with a number attached. The most common place to be, and the easiest to mistake for progress. This session is built to help you turn motion into one measurable result." },
  { key: 'emerging', name: 'Emerging Achiever', group: 'Achiever',
    read: "You have your first AI in production and can point to a result. Now the work is scaling it and building the discipline so it isn't a one-off. On the 21st: the mechanics of moving from first win to business-as-usual — and your questions will be among the most useful in the room." },
  { key: 'established', name: 'Established Achiever', group: 'Achiever',
    read: "AI is part of how you operate, not a special project. You're ahead of most of the room. Come to compare notes, pressure-test your approach, and — if you're willing — share what worked." },
];

const TOURISM_LINE = 'One pattern worth noticing: your team has been to a lot of AI destinations — demos, sessions, site visits — without moving in. Amit has a name for that. Come hear it on the 21st.';

const TOTAL = QUESTIONS.length; // 11

/* ---- scoring (mirrors the book's Believer/Achiever logic) ---- */
function computeResult(a) {
  const inProduction = a[0] >= 2;            // Q1: at least one capability in full production
  const measured = a[1] >= 3;                // Q2: a measured result against a baseline
  const isAchiever = inProduction && measured;
  let stageKey;
  if (isAchiever) {
    stageKey = (a[0] === 4 && a[8] === 4) ? 'established' : 'emerging';
  } else {
    const motion = (a[0] >= 1) || (a[3] === 1) || (a[10] >= 2); // any pilot/production, many parallel experiments, or 4+ "tours"
    if (motion) {
      stageKey = 'active';
    } else {
      let sum = 0;
      for (let i = 0; i < 10; i++) { sum += a[i]; }
      stageKey = sum <= 3 ? 'skeptic' : 'early';  // near-blank = Skeptic; some conviction/prep = Early Believer
    }
  }
  const aiTourist = (a[10] >= 2) && (a[0] <= 1); // toured 4+ but nothing/pilots only in production
  const idx = STAGES.map((s) => s.key).indexOf(stageKey);
  return { stageKey, stageIndex: idx, group: isAchiever ? 'Achiever' : 'Believer', aiTourist };
}

/* ---- anonymous metadata (no PII, no IP, no precise location) ---- */
function collectMeta(startedAt) {
  const u = new URLSearchParams(window.location.search);
  let tz = '';
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (e) { /* ignore */ }
  return {
    ts: new Date().toISOString(),
    completedInSec: Math.max(1, Math.round((Date.now() - startedAt) / 1000)),
    device: (window.matchMedia && window.matchMedia('(pointer:coarse)').matches) ? 'touch' : 'pointer',
    viewport: window.innerWidth + 'x' + window.innerHeight,
    language: navigator.language || null,
    timezone: tz || null,
    referrer: document.referrer || null,
    utm_source: u.get('utm_source'), utm_medium: u.get('utm_medium'), utm_campaign: u.get('utm_campaign'),
  };
}

/* ---- send result (anonymous). Never blocks the user. ---- */
function submitResult(payload) {
  try {
    if (CFG.DATA_ENDPOINT) {
      fetch(CFG.DATA_ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload), keepalive: true }).catch(() => {});
    }
  } catch (e) { /* ignore */ }
  try {
    const k = 'bva_submissions';
    const arr = JSON.parse(localStorage.getItem(k) || '[]');
    arr.push(payload);
    localStorage.setItem(k, JSON.stringify(arr));
  } catch (e) { /* ignore */ }
  if (!CFG.DATA_ENDPOINT) { console.log('[assessment] DATA_ENDPOINT not set — result logged locally only:', payload); }
}

export const BelieverAndAchiever = () => {
  const [answers, setAnswers] = useState(() => new Array(TOTAL).fill(null));
  const [demo, setDemo] = useState({ role: '', orgSize: '', industry: '' });
  const [step, setStep] = useState(0); // 0..TOTAL-1 = questions ; TOTAL = optional demographics
  const [result, setResult] = useState(null);
  const [showResult, setShowResult] = useState(false);

  const startedAtRef = useRef(Date.now());
  const assessmentRef = useRef(null);
  const resultRef = useRef(null);

  useEffect(() => {
    const prev = document.title;
    document.title = 'Believer or Achiever? — The AI-First CHRO';
    return () => { document.title = prev; };
  }, []);

  const answeredCount = useMemo(() => answers.filter((x) => x !== null).length, [answers]);
  const progressPct = Math.round((answeredCount / TOTAL) * 100);

  const scrollQuiz = useCallback(() => {
    const el = assessmentRef.current;
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 70;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, []);

  // reveal + scroll to result once it has rendered (result section is display:none until `show`)
  useEffect(() => {
    if (showResult && resultRef.current) {
      const y = resultRef.current.getBoundingClientRect().top + window.scrollY - 60;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, [showResult]);

  const selectOption = (k) => {
    setAnswers((prev) => {
      const nextArr = prev.slice();
      nextArr[step] = k;
      return nextArr;
    });
    // no auto-advance — the user taps "Next" to move on, and can change this answer or go Back first
  };

  const next = () => {
    if (step < TOTAL - 1) {
      if (answers[step] === null) return;
      setStep(step + 1);
      scrollQuiz();
    } else if (step === TOTAL - 1) {
      if (answers[step] === null) return;
      setStep(TOTAL);
      scrollQuiz();
    } else {
      finish();
    }
  };

  const prev = () => {
    if (step > 0) {
      setStep(step - 1);
      scrollQuiz();
    }
  };

  const finish = () => {
    const res = computeResult(answers);
    const stage = STAGES[res.stageIndex];
    // build + send anonymous payload
    const answerLabels = {};
    for (let q = 0; q < TOTAL; q++) {
      answerLabels['q' + (q + 1)] = {
        index: answers[q],
        question: QUESTIONS[q].t,
        label: (answers[q] !== null ? QUESTIONS[q].o[answers[q]] : null),
      };
    }
    const payload = {
      result: { stage: stage.name, stageKey: stage.key, group: res.group, aiTourist: res.aiTourist },
      answers: answerLabels,
      demographics: demo,
      meta: collectMeta(startedAtRef.current),
    };
    submitResult(payload);
    // Email the completed assessment (same email API as the waitlist form).
    // Fire-and-forget: never block the user from seeing their result.
    submitAssessmentResult(payload).catch((err) => {
      console.error('Assessment email failed:', err);
    });
    setResult(res);
    setShowResult(true);
  };

  const retake = (e) => {
    if (e && e.preventDefault) { e.preventDefault(); }
    setAnswers(new Array(TOTAL).fill(null));
    setDemo({ role: '', orgSize: '', industry: '' });
    setStep(0);
    setResult(null);
    setShowResult(false);
    startedAtRef.current = Date.now();
    scrollQuiz();
  };

  const stage = result ? STAGES[result.stageIndex] : null;
  const stageClass = (key) => 'rs-stage' + (result && result.stageKey === key ? ' is-current' : '');

  return (
    <div className="bva-page">
      <nav>
        <div className="nav-inner">
          <div className="nav-logo">The AI-First <span>CHRO</span></div>
          <div className="nav-tag">5-minute anonymous self-assessment</div>
        </div>
      </nav>

      {/* HERO */}
      <header className="hero">
        <div className="hero-inner">
          <div className="urgency-strip"><span className="urgency-dot"></span><span className="urgency-text">Free live webinar · Tue 21 July 2026 · 3 PM IST</span></div>
          <h1>Are You a Believer or an Achiever?
            <em>Where does your HR function really sit on AI? Find out in five minutes — anonymously.</em>
          </h1>
          <p className="hero-desc">Most HR leaders believe in AI. Far fewer can show a result. Answer ten honest questions and get your read instantly — then see where the <strong>whole room</strong> lands, live on 21 July with OnGrid HR Community and iona.ai.</p>
          <a href="#assessment" className="hero-cta">Start the assessment ↓</a>
          <div className="hero-stats">
            <div><div className="hero-stat-num">5 min</div><div className="hero-stat-label">To complete</div></div>
            <div><div className="hero-stat-num">10</div><div className="hero-stat-label">Honest questions</div></div>
            <div><div className="hero-stat-num">100%</div><div className="hero-stat-label">Anonymous</div></div>
          </div>
          <div className="author-strip">
            <div className="author-photo">AS</div>
            <div>
              <div className="author-name">Amit Srivastava</div>
              <div className="author-bio">Author, <em>The AI-First CHRO</em> · Founder, iona.ai</div>
              <div className="author-signed">Believer vs Achiever · from the book</div>
            </div>
          </div>
        </div>
      </header>

      {/* ASSESSMENT */}
      <section className="section-wrap-white" id="assessment" ref={assessmentRef}>
        <div className="narrow">
          <div className="section-label">The self-assessment</div>
          <h2 className="section-title">Where does your function actually sit?</h2>
          <p className="section-sub">Ten questions on the honest state of AI in your HR function, plus one on AI Tourism. Answer for how things really are — this is completely anonymous, so no one is watching.</p>
          <div className="quiz-card">
            <div className="progress"><div className="progress-bar" style={{ width: progressPct + '%' }}></div></div>
            <div className="quiz-body">
              {step < TOTAL ? (
                <>
                  <div className="q-count">Question {step + 1} of {TOTAL}</div>
                  <div className="q-text">{QUESTIONS[step].t}</div>
                  <div className="options">
                    {QUESTIONS[step].o.map((opt, k) => (
                      <button
                        type="button"
                        key={k}
                        className={'option' + (answers[step] === k ? ' selected' : '')}
                        onClick={() => selectOption(k)}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                  <div className="quiz-nav">
                    <button type="button" className="btn-back" onClick={prev} style={step === 0 ? { visibility: 'hidden' } : undefined}>← Back</button>
                    <button type="button" className="btn-next" onClick={next} disabled={answers[step] === null}>Next →</button>
                  </div>
                </>
              ) : (
                <>
                  <div className="q-count">Last step · optional</div>
                  <div className="q-text">A little about your function <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, fontWeight: 400, color: 'var(--mid-gray)' }}>— optional, still anonymous</span></div>
                  <div className="demo-grid">
                    <div>
                      <label className="demo-label">Your role</label>
                      <select className="demo-select" value={demo.role} onChange={(e) => setDemo({ ...demo, role: e.target.value })}>
                        <option value="">Prefer not to say</option>
                        <option>CHRO / CPO / Head of HR</option>
                        <option>HR Director / VP HR</option>
                        <option>Talent Acquisition</option>
                        <option>L&amp;D / Capability</option>
                        <option>HR Operations</option>
                        <option>People Analytics / HR Tech</option>
                        <option>Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="demo-label">Organisation size</label>
                      <select className="demo-select" value={demo.orgSize} onChange={(e) => setDemo({ ...demo, orgSize: e.target.value })}>
                        <option value="">Prefer not to say</option>
                        <option>Under 500</option>
                        <option>500–2,000</option>
                        <option>2,000–10,000</option>
                        <option>10,000+</option>
                      </select>
                    </div>
                    <div>
                      <label className="demo-label">Industry</label>
                      <select className="demo-select" value={demo.industry} onChange={(e) => setDemo({ ...demo, industry: e.target.value })}>
                        <option value="">Prefer not to say</option>
                        <option>BFSI</option>
                        <option>IT / Technology</option>
                        <option>Healthcare / Pharma</option>
                        <option>Manufacturing</option>
                        <option>Retail / Consumer</option>
                        <option>Services / Consulting</option>
                        <option>Public sector / PSU</option>
                        <option>Other</option>
                      </select>
                    </div>
                  </div>
                  <div className="quiz-nav">
                    <button type="button" className="btn-back" onClick={prev}>← Back</button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <button type="button" className="demo-skip" onClick={finish}>Skip</button>
                      <button type="button" className="btn-next" onClick={finish}>See my result →</button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="anon-note"><span>🔒</span><span>No name, company or email is collected. Nothing you answer can be traced back to you.</span></div>
        </div>
      </section>

      {/* RESULT */}
      <section className={'result-section' + (showResult ? ' show' : '')} id="result" ref={resultRef}>
        <div className="rs-hero">
          <div className="rs-hero-inner">
            <p className="rs-eyebrow">Your result</p>
            <p className="rs-group">{result ? ("You're " + (result.group === 'Achiever' ? 'an Achiever.' : 'a Believer.')) : "You're a Believer."}</p>
            <h1 className="rs-stage-title">{stage ? stage.name : 'Active Believer'}</h1>
            <div className="rs-hero-rule" aria-hidden="true"></div>
            <p className="rs-read">{stage ? stage.read : '—'}</p>
            {result && result.aiTourist && (
              <div className="rs-tourism">{TOURISM_LINE}</div>
            )}
            <div className="rs-hero-cta">
              {CFG.DEEPER_ANALYSIS_URL ? (
                <a className="rs-btn rs-btn--deeper" href={CFG.DEEPER_ANALYSIS_URL}>Get the deeper analysis</a>
              ) : (
                <span className="rs-btn rs-btn--deeper is-pending">Deeper analysis — opens after the webinar</span>
              )}
              <p className="rs-cta-note">A tailored breakdown of your stage — and the one move that gets you to the next.</p>
            </div>
          </div>
        </div>

        <div className="rs-spectrum-wrap">
          <div className="rs-spectrum-inner">
            <div className="rs-spectrum-head">
              <p className="rs-eyebrow rs-eyebrow-dark">The full spectrum</p>
              <h2 className="rs-spectrum-title">Believer <span>→</span> Achiever</h2>
              <p className="rs-spectrum-sub">Most HR leaders believe in AI. Far fewer achieve results with it. What separates them is execution discipline — not enthusiasm, intelligence or budget.</p>
            </div>

            <div className="rs-spine">
              <div className={stageClass('skeptic')} data-stage="skeptic">
                <div className="rs-band">
                  <span className="rs-band-name">Believers</span>
                  <span className="rs-band-note">They talk about AI.</span>
                </div>
                <div className="rs-stage-row">
                  <div className="rs-spine-col"><span className="rs-node" aria-hidden="true"></span></div>
                  <div className="rs-stage-content">
                    <div className="rs-stage-top">
                      <span className="rs-stage-num">Stage 01</span>
                      <span className="rs-here">You are here</span>
                    </div>
                    <h3 className="rs-stage-name">Skeptic</h3>
                    <p className="rs-stage-desc">Waiting for proof. Not yet convinced AI belongs in HR — and wants evidence before investing.</p>
                  </div>
                </div>
              </div>

              <div className={stageClass('early')} data-stage="early">
                <div className="rs-stage-row">
                  <div className="rs-spine-col"><span className="rs-node" aria-hidden="true"></span></div>
                  <div className="rs-stage-content">
                    <div className="rs-stage-top">
                      <span className="rs-stage-num">Stage 02</span>
                      <span className="rs-here">You are here</span>
                    </div>
                    <h3 className="rs-stage-name">Early Believer</h3>
                    <p className="rs-stage-desc">Convinced AI matters for HR, but nothing is on the ground yet. The gap now is action, not conviction.</p>
                  </div>
                </div>
              </div>

              <div className={stageClass('active')} data-stage="active">
                <div className="rs-stage-row">
                  <div className="rs-spine-col"><span className="rs-node" aria-hidden="true"></span></div>
                  <div className="rs-stage-content">
                    <div className="rs-stage-top">
                      <span className="rs-stage-num">Stage 03</span>
                      <span className="rs-tag-common">Most common</span>
                      <span className="rs-here">You are here</span>
                    </div>
                    <h3 className="rs-stage-name">Active Believer</h3>
                    <p className="rs-stage-desc">Busy with pilots, demos and evaluations — but nothing in full production with a number attached. Lots of motion, easily mistaken for progress.</p>
                  </div>
                </div>
              </div>

              <div className={stageClass('emerging')} data-stage="emerging">
                <div className="rs-divide" aria-hidden="true">
                  <div className="rs-divide-rule"><span>The Believer–Achiever Line</span></div>
                  <div className="rs-band rs-band--achiever">
                    <span className="rs-band-name">Achievers</span>
                    <span className="rs-band-note">They deliver with it — in production, measured.</span>
                  </div>
                </div>
                <div className="rs-stage-row">
                  <div className="rs-spine-col"><span className="rs-node" aria-hidden="true"></span></div>
                  <div className="rs-stage-content">
                    <div className="rs-stage-top">
                      <span className="rs-stage-num">Stage 04</span>
                      <span className="rs-here">You are here</span>
                    </div>
                    <h3 className="rs-stage-name">Emerging Achiever</h3>
                    <p className="rs-stage-desc">First AI in production, first measured result. Now building the discipline to make it repeatable — and to scale it.</p>
                  </div>
                </div>
              </div>

              <div className={stageClass('established')} data-stage="established">
                <div className="rs-stage-row">
                  <div className="rs-spine-col"><span className="rs-node" aria-hidden="true"></span></div>
                  <div className="rs-stage-content">
                    <div className="rs-stage-top">
                      <span className="rs-stage-num">Stage 05</span>
                      <span className="rs-here">You are here</span>
                    </div>
                    <h3 className="rs-stage-name">Established Achiever</h3>
                    <p className="rs-stage-desc">AI is business-as-usual, embedded in how the function operates — not a special project with a steering committee.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rs-footer">
              <a className="rs-retake" href="#assessment" onClick={retake}>Retake the assessment</a>
            </div>
          </div>
        </div>
      </section>

      {/* WHY IT MATTERS */}
      <section className="section-dark">
        <div className="wrap">
          <div className="research-label">Why this matters</div>
          <h3 className="research-title">Adoption is easy. Results are rare.</h3>
          <div className="pillars">
            <div className="pillar"><div className="pillar-stat">47%</div><div className="pillar-text">of CHROs haven't defined how to measure AI value.</div><div className="pillar-src">Gartner, 2026</div></div>
            <div className="pillar"><div className="pillar-stat">74%</div><div className="pillar-text">of AI projects fail to move beyond the pilot stage.</div><div className="pillar-src">BCG Research</div></div>
            <div className="pillar"><div className="pillar-stat">78%</div><div className="pillar-text">of companies use AI, but 74% aren't seeing real value.</div><div className="pillar-src">Industry survey</div></div>
          </div>
        </div>
      </section>

      {/* WEBINAR */}
      <section className="section-wrap">
        <div className="narrow">
          <div className="section-label">The live session</div>
          <h2 className="section-title">AI in HR: Are You a Believer or an Achiever?</h2>
          <p className="section-sub">A candid panel on the gap between adopting AI and getting results — and the execution discipline that closes it. Bring your assessment result; we'll show you where the whole room landed.</p>
          <div className="detail-card">
            <div className="detail-row"><div className="detail-key">When</div><div className="detail-val">Tuesday, 21 July 2026 · 3:00–4:00 PM IST · Online</div></div>
            <div className="detail-row"><div className="detail-key">Hosts</div><div className="detail-val">OnGrid HR Community, with iona.ai</div></div>
            <div className="detail-row"><div className="detail-key">Panel</div><div className="detail-val">Amit Srivastava (Author, <em>The AI-First CHRO</em>) · Tulanahina Maity (CHRO, Anand Rathi Group)</div></div>
            <div className="detail-row"><div className="detail-key">Format</div><div className="detail-val">35-minute moderated panel + ~12 minutes of your questions</div></div>
            <div className="detail-row"><div className="detail-key">Bonus</div><div className="detail-val">A few attendees receive a signed copy of the book</div></div>
          </div>
          <p className="section-sub" style={{ marginTop: 20, marginBottom: 0 }}>Registration is handled by OnGrid — you'll have received the join link in your confirmation email.</p>
        </div>
      </section>

      <footer>
        <div className="footer-inner">
          <div className="footer-brand">The AI-First <span>CHRO</span></div>
          <div className="footer-tagline">The missing playbook for HR leaders in an AI-first world.</div>
          <div className="footer-legal">© Amit Srivastava 2026 · www.AIFirstCHRO.com · This self-assessment is anonymous.</div>
        </div>
      </footer>
    </div>
  );
};

export default BelieverAndAchiever;
